import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

export function HomePage() {
  return (
    <div className="relative min-h-screen">
      {/* Gradient mesh background */}
      <div className="absolute inset-0 bg-gradient-mesh opacity-30 dark:opacity-50" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <div className="inline-block mb-6">
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-brand-100 dark:bg-brand-950/50 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-900">
              🔒 Privacy-First Platform
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6 animate-fade-in">
            Welcome to{' '}
            <span className="bg-gradient-to-r from-brand-600 to-accent-500 bg-clip-text text-transparent">
              Privacy Forum
            </span>
          </h1>

          <p className="text-xl text-slate-600 dark:text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            A privacy-centric forum with WebAuthn authentication, alias-based identity, and secure marketplace.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/forum">
              <Button size="lg" className="shadow-colored">
                Browse Forum
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" size="lg">
                Get Started
              </Button>
            </Link>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          <Card
            hover
            className="group relative overflow-hidden bg-gradient-to-br from-white to-slate-50 dark:from-dark-card dark:to-dark-bg-secondary border-slate-200 dark:border-dark-border"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/10 dark:bg-brand-500/5 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-brand-500/20 transition-all" />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center mb-4 shadow-glow">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                WebAuthn Security
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Passwordless authentication using WebAuthn for maximum security.
              </p>
            </div>
          </Card>

          <Card
            hover
            className="group relative overflow-hidden bg-gradient-to-br from-white to-slate-50 dark:from-dark-card dark:to-dark-bg-secondary border-slate-200 dark:border-dark-border"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent-500/10 dark:bg-accent-500/5 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-accent-500/20 transition-all" />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center mb-4 shadow-glow-accent">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                Alias System
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Use different aliases per topic while maintaining privacy.
              </p>
            </div>
          </Card>

          <Card
            hover
            className="group relative overflow-hidden bg-gradient-to-br from-white to-slate-50 dark:from-dark-card dark:to-dark-bg-secondary border-slate-200 dark:border-dark-border"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 dark:bg-emerald-500/5 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-emerald-500/20 transition-all" />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/50">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                Secure Marketplace
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Trade safely with booking verification and encrypted messaging.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
